import "./SubmissionOverlay.scss";

import { useEffect, useRef } from "react";

type SubmissionOverlayProps = {
  isVisible: boolean;
  progress?: number;
  message?: string;
  isComplete?: boolean;
};

const SubmissionOverlay = ({
  isVisible,
  progress,
  message = "Processing your submission...",
  isComplete = false,
}: SubmissionOverlayProps) => {
  const cleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!isVisible || isComplete) {
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
      return;
    }

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      return "Your intake form is being submitted. Leaving now may cause data loss.";
    };

    const handlePopState = (e: PopStateEvent) => {
      if (
        window.confirm(
          "Your intake form is being submitted. Are you sure you want to leave?",
        )
      ) {
        return;
      }
      e.preventDefault();
      window.history.pushState(null, "", window.location.href);
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("popstate", handlePopState);
    window.history.pushState(null, "", window.location.href);

    cleanupRef.current = () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);
    };

    return cleanupRef.current;
  }, [isVisible, isComplete]);

  if (!isVisible) return null;

  return (
    <div className="submission-overlay">
      <div className="submission-content">
        <div className="submission-icon">
          <div className="spinner" />
        </div>

        <h2 className="submission-title">Submitting Intake Form</h2>

        <p className="submission-message">{message}</p>

        {progress !== undefined && (
          <div className="progress-container">
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${Math.min(progress, 100)}%` }}
              />
            </div>
            <span className="progress-text">
              {Math.round(progress)}% complete
            </span>
          </div>
        )}

        <div className="submission-warning">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="warning-icon"
          >
            <path
              d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span>
            Please do not close this window or navigate away. Your submission is
            being processed.
          </span>
        </div>
      </div>
    </div>
  );
};

export default SubmissionOverlay;
